/**
 * #1267 (found by the PR #1262 security review): a connection with a missing credential is never
 * Ready. The one rule the save, the connection test and the editor read.
 *
 * A save that leaves a stored credential out of its body deletes it (`DataflowService.update`,
 * `writeFields`), and a blank, null or masked value keeps the stored one. So a credential goes
 * missing in two ways (a platform-managed field, which the person never sends, never counts):
 *
 * - **removed**: the field held a stored secret before the save and has none after it;
 * - **required and empty**: the seed requires the field with no condition (`validation.required`
 *   true and no `dependencies`, so SFTP's password-or-key pair is never read as both required)
 *   and nothing is stored for it.
 *
 * Either way the earlier test no longer speaks for the connection: `last_validation` becomes the
 * `CREDENTIAL_MISSING` block below (status `failed`), the setup state falls to `draft` (Setup
 * incomplete), and no pass is recorded until the credential is set and a test passes.
 */

/** `last_validation.errorCode` of a connection a credential is missing from. */
export const CREDENTIAL_MISSING_CODE = "CREDENTIAL_MISSING";

/** One stored field row, as far as this rule needs it. */
export interface StoredCredentialField {
  hasSecret: boolean;
}

interface CredentialDefinition {
  id: string;
  label: string;
  requiredAlways: boolean;
  /**
   * PR #1312 review: a platform-managed credential (`isPlatformManaged`, PCC's client secret and
   * certificate pair) is never sent by the person: the editor, the setup page and the MCP tools
   * all leave it out, so nothing is ever stored for it. It is never "missing".
   */
  platformManaged: boolean;
}

function credentialDefinitions(fieldDefinitions: unknown): CredentialDefinition[] {
  if (!Array.isArray(fieldDefinitions)) return [];
  const out: CredentialDefinition[] = [];
  for (const entry of fieldDefinitions) {
    if (!entry || typeof entry !== "object") continue;
    const field = entry as {
      id?: unknown;
      name?: unknown;
      label?: unknown;
      isCredential?: unknown;
      validation?: { required?: { value?: unknown } } | null;
      dependencies?: unknown;
      isPlatformManaged?: unknown;
    };
    if (field.isCredential !== true || typeof field.id !== "string") continue;
    const label =
      typeof field.name === "string" && field.name.trim() !== ""
        ? field.name.trim()
        : typeof field.label === "string" && field.label.trim() !== ""
          ? field.label.trim()
          : field.id;
    const conditional = Array.isArray(field.dependencies) && field.dependencies.length > 0;
    out.push({
      id: field.id,
      label,
      requiredAlways: field.validation?.required?.value === true && !conditional,
      platformManaged: field.isPlatformManaged === true,
    });
  }
  return out;
}

/**
 * The credential field ids missing after a save, in the definition's order: removed by it (a
 * stored secret before, none after), or required with no condition and empty. `before` is null
 * where there is no earlier state to compare with (a connection test of what is stored).
 */
export function missingCredentialFieldIds(input: {
  fieldDefinitions: unknown;
  before: ReadonlyMap<string, StoredCredentialField> | null;
  after: ReadonlyMap<string, StoredCredentialField>;
}): string[] {
  return credentialDefinitions(input.fieldDefinitions)
    .filter((field) => {
      if (field.platformManaged) return false;
      const now = input.after.get(field.id)?.hasSecret === true;
      if (now) return false;
      const removed = input.before?.get(field.id)?.hasSecret === true;
      return removed || field.requiredAlways;
    })
    .map((field) => field.id);
}

/** "Password" reads "password", "API Password" reads "API password" (an acronym keeps its case). */
function inSentence(label: string): string {
  return label
    .split(" ")
    .map((word) =>
      /^[A-Z][a-z]/.test(word) ? `${word.charAt(0).toLowerCase()}${word.slice(1)}` : word,
    )
    .join(" ");
}

function joinNames(names: readonly string[]): string {
  const the = names.map((name) => `the ${name}`);
  if (the.length <= 1) return the.join("");
  return `${the.slice(0, -1).join(", ")} and ${the[the.length - 1]}`;
}

/**
 * The sentence stored on `last_validation.message` with `CREDENTIAL_MISSING_CODE`, naming each
 * missing field by its label: "Setup incomplete. Enter the password and validate the connection."
 */
export function credentialMissingMessage(
  fieldDefinitions: unknown,
  missingIds: readonly string[],
): string {
  const labels = new Map(credentialDefinitions(fieldDefinitions).map((f) => [f.id, f.label]));
  const names = missingIds.map((id) => inSentence(labels.get(id) ?? id));
  if (names.length === 1) {
    return `Setup incomplete. Enter ${joinNames(names)} and validate the connection.`;
  }
  const what = names.length > 0 ? joinNames(names) : "the credentials";
  return `Setup incomplete. Enter ${what}, then validate the connection.`;
}

/**
 * PR #1312 review: the 422 issue Run now and the schedule picker answer for a connection a
 * credential is missing from, in the stored block's words, so the refusal names the field.
 */
export function credentialMissingIssue(name: string, message: string | null): string {
  return message
    ? `${name}: ${message}`
    : `${name} is missing a credential. Enter it and validate the connection.`;
}

/** True when a stored `last_validation` is the `CREDENTIAL_MISSING` block. */
export function isCredentialMissingValidation(lastValidation: unknown): boolean {
  return (
    typeof lastValidation === "object" &&
    lastValidation !== null &&
    (lastValidation as { errorCode?: unknown }).errorCode === CREDENTIAL_MISSING_CODE
  );
}
